import React, { useState } from 'react';
import { Modal, Button, Dropdown } from 'react-bootstrap';
import './PlayerAssetPreviewModal.css';

export type PlayerAssetType = 'qrCode' | 'playerIdCard';

interface PlayerAssetPreviewModalProps {
  show: boolean;
  assetType: PlayerAssetType;
  assetUrl?: string;
  playerName?: string;
  generatedAt?: string;
  onClose: () => void;
  onDownload?: () => void;
}

const typeCopyMap: Record<PlayerAssetType, { title: string; description: string }> = {
  qrCode: {
    title: 'QR Code',
    description: 'Scan this QR to verify player details instantly.'
  },
  playerIdCard: {
    title: 'Player ID Card',
    description: 'Use this digital ID for on-ground verification.'
  }
};

const PlayerAssetPreviewModal: React.FC<PlayerAssetPreviewModalProps> = ({
  show,
  assetType,
  assetUrl,
  playerName,
  generatedAt,
  onClose,
  onDownload
}) => {
  const [shareSuccess, setShareSuccess] = useState(false);
  const isPdf = assetType === 'playerIdCard';
  const copy = typeCopyMap[assetType];

  const handleShareEmail = () => {
    if (!assetUrl) return;
    
    const subject = encodeURIComponent(`${copy.title} - ${playerName || 'Player'}`);
    const body = encodeURIComponent(`${copy.description}\n\nView here: ${assetUrl}`);
    const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
    
    window.location.href = mailtoLink;
  };

  const handleShareWhatsApp = () => {
    if (!assetUrl) return;
    
    const message = encodeURIComponent(`${copy.title} - ${playerName || 'Player'}\n${copy.description}\n\n${assetUrl}`);
    const whatsappLink = `https://wa.me/?text=${message}`;
    
    window.open(whatsappLink, '_blank');
  };

  const handleShareCopy = async () => {
    if (!assetUrl) return;

    try {
      await navigator.clipboard.writeText(assetUrl);
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Modal 
      show={show} 
      centered 
      onHide={onClose} 
      size="lg" 
      backdrop="static" 
      keyboard
      dialogClassName="player-asset-modal-dialog"
      contentClassName="player-asset-modal-content"
    >
      <Modal.Header closeButton>
        <div className="d-flex flex-column">
          <Modal.Title>{copy.title}</Modal.Title>
          <small className="text-muted">
            {playerName ? `${playerName} • ` : ''}{copy.description}
          </small>
        </div>
      </Modal.Header>
      <Modal.Body>
        {!assetUrl ? (
          <div className="text-center py-5">
            <p className="text-muted mb-1">No {copy.title.toLowerCase()} available for this player yet.</p>
            <small>Please ensure the document is generated from the backend.</small>
          </div>
        ) : (
          <div style={{ 
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {isPdf ? (
              <iframe
                title={`${copy.title} Preview`}
                src={`${assetUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=page-width`}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  height: '520px',
                  border: '1px solid #e4e7ec',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'block'
                }}
              />
            ) : (
              <div
                style={{
                  border: '1px solid #e4e7ec',
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  background: '#f8fafc'
                }}
              >
                <img
                  src={assetUrl}
                  alt={`${copy.title} for ${playerName || 'player'}`}
                  style={{ maxWidth: '320px', width: '100%' }}
                />
              </div>
            )}
            {generatedAt && (
              <p className="text-center text-muted small mt-3 mb-0">
                Generated on {new Date(generatedAt).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <div className="d-flex gap-2 align-items-center">
          <Button variant="primary" onClick={onDownload} disabled={!assetUrl}>
            Download {copy.title}
          </Button>
          <Dropdown>
            <Dropdown.Toggle 
              variant="outline-primary" 
              disabled={!assetUrl}
              title="Share"
            >
              <i className={shareSuccess ? "ri-check-line" : "ri-share-line"}></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleShareEmail}>
                <i className="ri-mail-line me-2"></i>
                Share via Email
              </Dropdown.Item>
              <Dropdown.Item onClick={handleShareWhatsApp}>
                <i className="ri-whatsapp-line me-2"></i>
                Share via WhatsApp
              </Dropdown.Item>
              <Dropdown.Item onClick={handleShareCopy}>
                <i className="ri-file-copy-line me-2"></i>
                {shareSuccess ? 'Copied!' : 'Copy Link'}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PlayerAssetPreviewModal;

